import { Button, ButtonGroupRow, CheckBox, InfoIconWithText, Table, TextArea } from '@isdd/idsk-ui-kit/index'
import {
    KrisToBeRights,
    NewNoteUi,
    NoteItemUi,
    useAddEvaluationHook,
    useAddResponseHook,
    useGetEvaluations,
} from '@isdd/metais-common/api/generated/kris-swagger'
import { AttributeProfile } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { MutationFeedback, QueryFeedback } from '@isdd/metais-common/index'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ColumnDef } from '@tanstack/react-table'

import { IResultCall } from '@/components/views/evaluation/EvaluationView'
import styles from '@/components/views/evaluation/evaluationView.module.scss'

interface IKSEvaluationRowProps {
    uuid?: string
    entityId: string
    isvsAttributes?: AttributeProfile
    dataRights?: KrisToBeRights
    isGlobalAllowed: boolean
}

interface IDetailISVSColumn {
    id: string
    name: string
    tooltip: string
    lastEvaliation?: string
    createdBy?: string
    isApproved: boolean
    evaluation?: string
    response?: string
}

interface TableMeta {
    checkedAll: boolean
    isRowEdit: boolean
}

export const KSEvaluationRow: React.FC<IKSEvaluationRowProps> = ({ uuid, entityId, isvsAttributes, dataRights, isGlobalAllowed }) => {
    const { t } = useTranslation()
    const { data: evalData, isError, isLoading, refetch, isFetching } = useGetEvaluations(entityId, uuid ?? '', 'KS')
    const { register, handleSubmit, setValue, getValues } = useForm<Array<IDetailISVSColumn>>()
    const [isLoadingAddData, setLoadingAddData] = useState<boolean>(false)
    const [isErrorAddData, setErrorAddData] = useState<boolean>(false)
    const [isEditRow, setEditRow] = useState<boolean>(false)
    const saveIsvsHook = useAddEvaluationHook()
    const saveResponseIsvsHook = useAddResponseHook()
    const [resultSuccessApiCall, setResultSuccessApiCall] = useState<IResultCall>({ isSuccess: false, message: '' })

    const onSubmit = (formData: IDetailISVSColumn[]) => {
        setEditRow(false)
        setLoadingAddData(true)
        const convertData = Object.values(formData)
        if (dataRights?.inEvaluation) {
            const fetchData: NewNoteUi = {
                values: convertData?.map((item: IDetailISVSColumn) => {
                    return { name: item.id, value: item.evaluation }
                }),
                state: {
                    values: convertData?.map((item: IDetailISVSColumn) => {
                        return { name: item.id, value: item.isApproved }
                    }),
                },
            }
            saveIsvsHook(entityId, uuid ?? '', 'KS', fetchData)
                .then(() => {
                    refetch()
                    setResultSuccessApiCall({ isSuccess: true, message: t('evaluation.saveSuccess') })
                })
                .catch(() => {
                    setErrorAddData(false)
                })
                .finally(() => {
                    setLoadingAddData(false)
                })
        } else {
            const fetchData: NewNoteUi = {
                values: convertData?.map((item: IDetailISVSColumn) => {
                    return { name: item.id, value: item.response }
                }),
            }

            saveResponseIsvsHook(entityId, uuid ?? '', 'KS', fetchData)
                .then(() => {
                    refetch()
                    setResultSuccessApiCall({ isSuccess: true, message: t('evaluation.saveSuccess') })
                })
                .catch(() => {
                    setErrorAddData(false)
                })
                .finally(() => {
                    setLoadingAddData(false)
                })
        }
    }

    const [checkedAll, setCheckedAll] = useState(false)
    const tableMeta: TableMeta = {
        checkedAll: checkedAll,
        isRowEdit: isEditRow,
    }

    const handleAllCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCheckedAll(event.target.checked)

        const values = Object.values(getValues())
        for (let i = 0; i < values.length; i++) {
            setValue(`${i}.isApproved`, event.target.checked)
        }
    }

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        setCheckedAll(
            Object.values(getValues())
                .filter((_, i) => i !== index)
                .every((val) => val.isApproved) && event.target.checked,
        )
    }

    useEffect(() => {
        evalData?.state?.values?.forEach((state, index) => {
            setValue(`${index}.isApproved`, state.value ?? false)
        })
        setCheckedAll(evalData?.state?.values?.every((state) => state.value) ?? false)
    }, [evalData, setValue])

    const columnsDetail: Array<ColumnDef<IDetailISVSColumn>> = [
        {
            accessorFn: (row) => row?.name,
            header: '',
            id: 'name',
            cell: ({ row }) => {
                return (
                    <InfoIconWithText key={row?.original?.name} tooltip={row?.original?.tooltip} label={row?.original?.name}>
                        {row?.original?.name}
                        <input readOnly hidden {...register(`${row?.index}.id`)} key={row?.original?.id} value={row?.original?.id} />
                    </InfoIconWithText>
                )
            },
        },
        {
            accessorFn: (row) => row?.lastEvaliation,
            header: t('evaluation.detailTable.lastEvaluation'),
            id: 'lastEvaluation',
            cell: (ctx) => ctx.row?.original?.lastEvaliation,
        },
        {
            accessorFn: (row) => row?.response,
            header: t('evaluation.detailTable.createdBy'),
            id: 'createdBy',
            cell: (ctx) => {
                return !(ctx.table.options.meta as TableMeta)?.isRowEdit || dataRights?.inEvaluation ? (
                    ctx.row?.original?.response
                ) : (
                    <TextArea rows={3} defaultValue={ctx.row?.original?.response} {...register(`${ctx.row?.index}.response`)} />
                )
            },
        },
        {
            accessorFn: (row) => row?.isApproved,
            id: 'isApproved',
            header: (ctx) => {
                return (
                    <div className="govuk-checkboxes govuk-checkboxes--small">
                        <CheckBox
                            label=""
                            disabled={!(ctx.table.options.meta as TableMeta)?.isRowEdit || !dataRights?.inEvaluation}
                            name="checkbox-all"
                            id="checkbox-all"
                            value="checkbox-all"
                            checked={(ctx.table.options.meta as TableMeta)?.checkedAll}
                            onChange={(event) => handleAllCheckboxChange(event)}
                            title={t('table.selectAllItems')}
                        />
                    </div>
                )
            },
            cell: ({ row, table }) => {
                return (
                    <div className="govuk-checkboxes govuk-checkboxes--small">
                        <CheckBox
                            {...register(`${row?.index}.isApproved`)}
                            label=""
                            disabled={!(table.options.meta as TableMeta)?.isRowEdit || !dataRights?.inEvaluation}
                            id={`${row?.index}.isApproved`}
                            onChange={(event) => handleCheckboxChange(event, row.index)}
                        />
                    </div>
                )
            },
        },
        {
            accessorFn: (row) => row?.evaluation,
            header: (ctx) => {
                return (
                    <div className={styles.customHEader}>
                        {t('evaluation.detailTable.evaluation')}
                        {!isGlobalAllowed &&
                            (!(ctx.table.options.meta as TableMeta)?.isRowEdit ? (
                                <Button className={styles.headerBtn} label={t('evaluation.changeBtn')} onClick={() => setEditRow(!isEditRow)} />
                            ) : (
                                <ButtonGroupRow>
                                    <Button label={t('evaluation.saveBtn')} className={styles.headerBtn} type="submit" />
                                    <Button
                                        variant="secondary"
                                        className={styles.headerBtn}
                                        label={t('evaluation.cancelBtn')}
                                        onClick={() => setEditRow(false)}
                                    />
                                </ButtonGroupRow>
                            ))}
                    </div>
                )
            },
            id: 'evaluation',
            cell: ({ row, table }) => {
                return (table.options.meta as TableMeta)?.isRowEdit && dataRights?.inEvaluation ? (
                    <TextArea {...register(`${row?.index}.evaluation`)} rows={3} defaultValue={row?.original?.evaluation} />
                ) : (
                    <>{row?.original?.evaluation}</>
                )
            },
        },
    ]
    const mappedData = (apiData?: NoteItemUi): Array<IDetailISVSColumn> => {
        return (
            apiData?.state?.values?.map((item) => {
                return {
                    id: item.name,
                    name: isvsAttributes?.attributes?.find((att) => att.technicalName === item.name)?.name ?? '',
                    tooltip: isvsAttributes?.attributes?.find((att) => att.technicalName === item.name)?.description ?? '',
                    lastEvaliation: apiData?.evaluations?.[apiData?.evaluations?.length - 1]?.values?.find((e) => e.name === item.name)?.value ?? '',
                    createdBy: apiData?.evaluations?.[apiData?.evaluations?.length - 1]?.noteVersionUi?.createdBy ?? '',
                    isApproved: apiData?.state?.values?.find((e) => e.name === item.name)?.value,
                    evaluation: apiData?.evaluations?.[apiData?.evaluations?.length - 1]?.values?.find((e) => e.name === item.name)?.value ?? '',
                    response: apiData?.responses?.[apiData?.responses?.length - 1]?.values?.find((e) => e.name === item.name)?.value ?? '',
                } as IDetailISVSColumn
            }) ?? []
        )
    }

    return (
        <QueryFeedback loading={isLoading || isLoadingAddData || isFetching} error={isError || isErrorAddData}>
            <MutationFeedback
                success={resultSuccessApiCall.isSuccess}
                successMessage={resultSuccessApiCall.message}
                onMessageClose={() => setResultSuccessApiCall({ isSuccess: false, message: '' })}
            />
            <div className={styles.expandableRowContent}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Table columns={columnsDetail} data={mappedData(evalData)} tableMeta={tableMeta} />
                </form>
            </div>
        </QueryFeedback>
    )
}
